import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import FilterPanel from "./FilterPanel.tsx";
import CardBlock from "./CardBlock";
import queryString from "query-string";
import { Pagination } from "../../ui";
import { useMatch } from "react-router";
import { Loading } from "../../utils";
import { CatalogSorting, ProductFetchResponseType, CategoryType, CatalogFilter } from "../../types";
import useCatalogFilterParams from "./useCatalogFilterParams";
import useWishList from "../wishlist/useWishList";
import useCart from "../cart/useCart";
import { ProductType } from "../../types";
import CatalogFilterTags from "./CatalogFilterTags.tsx";
import CatalogNoItems from "./CatalogNoItems.tsx";
import { fetchJson } from "../../utils";
import { useAppState } from "../../context/useAppState";
import lodash from 'lodash';

const { isEqual } = lodash;

const PER_PAGE = 12;

// Memoize CardBlock to prevent unnecessary re-renders
const MemoizedCardBlock = React.memo(CardBlock);

// Helper function to build filter params
const buildFilterParams = (filters: Omit<CatalogFilter, 'sorting'>) => {
    return Object.keys(filters).reduce<Record<string, unknown>>((acc, key) => {
        const filterValue = filters[key as keyof typeof filters];

        if (filterValue !== undefined) {
            if (['price', 'duration', 'amount'].includes(key)) {
                if (Array.isArray(filterValue) && filterValue.length === 2) {
                    acc[key] = {
                        operator: 'between',
                        value: filterValue,
                    };
                }
            } else if (key === 'keyword' && typeof filterValue === 'string') {
                acc['__query'] = filterValue;
            } else if (key === 'categories') {
                if (Array.isArray(filterValue) && filterValue.length > 0) {
                    acc[key] = {
                        operator: 'in',
                        value: filterValue,
                    };
                }
            } else {
                acc[key] = filterValue;
            }
        }

        return acc;
    }, {});
};

export const Catalog = () => {
    const {
        filters,
        updateFilter,
        resetFilters
    } = useCatalogFilterParams();
    const match = useMatch('/catalog/:slug');
    const [categories] = useAppState<CategoryType[]>("categories", []);

   

    const { isInWishlist } = useWishList();
    const { isInCart } = useCart();

    // Extract sorting and rest of filters
    const { sorting = ['default', ''] as CatalogSorting, ...restFilters } = filters;
    const [sort, order] = sorting;

    // Get current page from URL
    const queryObject = useMemo(() => queryString.parse(location.search), [location.search]);
    
    // Track previous filters to detect changes
    const prevFilters = useRef(filters);
    const page = useMemo(() => {
        // Check if filters have changed
        if (!isEqual(prevFilters.current, filters)) {
            prevFilters.current = filters;
            return 1;
        }
        return Number(queryObject.page || 1);
    }, [queryObject.page, filters]);
   
    const allVideoRefs = useRef<{ [key: number]: HTMLVideoElement }>({});

    // Store fetch results in app state
    const [productFetchResult, setProductFetchResult] = useState<ProductFetchResponseType>({
        data: [],
        total: 0,
        pagination: undefined
    });

    // States for loading and error
    const [isLoading, setLoading] = useState(true);
    const [, setError] = useState(false);
    const [sortingFactor, setSortingFactor] = useState<number>(0);
    const lastCategory = useRef<number | null>(null);       

    useEffect(() => {
        if (match?.params.slug) {
            const matchedCategory = categories.find((cat: CategoryType) => cat.slug === match.params.slug);
            if (matchedCategory && matchedCategory.id !== lastCategory.current) {
                updateFilter('categories', [matchedCategory.id]);
                lastCategory.current = matchedCategory.id;
            }
        }
       
    }, [match?.params.slug, categories, updateFilter, lastCategory]);

    // Reset filters if no query parameters are present when user clicks on breadcrumb link
    useEffect(() => {
        if (Object.keys(queryObject).length === 0) {
            resetFilters();
        }
    }, [queryObject])

   

    // Build API request parameters
    const filterParams = useMemo(() => {
        const filters = { ...restFilters };
        
        if (match?.params.slug ) {
            const category = categories.find((cat: CategoryType) => cat.slug === match.params.slug);
            if (category?.id && category.id !== lastCategory.current) {
                filters.categories = [category.id];
            }
        }
        return buildFilterParams(filters);
    }, [restFilters, match?.params.slug, categories, lastCategory]);
    

    const uriParams = useMemo(() => {
        const params = new URLSearchParams();
        params.set('_page', page.toString());
        params.set('_perPage', PER_PAGE.toString());

        if (sort) {
            params.set('_sort', sort);
            params.set('_order', order || 'asc');
        }

        if (Object.keys(filterParams).length > 0) {
            params.set('_filter', JSON.stringify(filterParams));
        }

        return params;
    }, [page, sort, order, filterParams]);

    const fetchUri = useMemo(() =>
        `/api/catalog?${uriParams.toString()}`,
        [uriParams]
    );

    // Fetch data function
    const fetchData = useCallback(async () => {
        if (!fetchUri) return;

        setLoading(true);
        setError(false);
        try {
            const { json } = await fetchJson<ProductFetchResponseType>(fetchUri);
            setProductFetchResult(json);
        } catch (error) {
            console.error('Error fetching products:', error);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [fetchUri, sortingFactor]);

    // Fetch data effect
    useEffect(() => {
        fetchData();
    }, [fetchUri]);

    const handleVideoRef = useCallback((id: number, ref: HTMLVideoElement | null) => {
        if (ref) {
            allVideoRefs.current[id] = ref;
        } else {
            delete allVideoRefs.current[id];
        }
    }, []);

    const pauseAllExcept = useCallback((currentId: number) => {
        Object.entries(allVideoRefs.current).forEach(([id, ref]) => {
            if (Number(id) !== currentId && ref) {
                ref.pause();
            }
        });
    }, []);

    const setSorting = useCallback((sorting: CatalogSorting) => {
        updateFilter('sorting', sorting);
    }, [updateFilter]);

    const sortingOptions = useMemo(() => [
        { value: { field: 'popularity', order: '' }, label: 'Sort By Popularity' },
        { value: { field: 'price', order: 'asc' }, label: 'Lower Price First' },
        { value: { field: 'price', order: 'desc' }, label: 'Higher Price First' },
        { value: { field: 'clipProperties.duration', order: 'asc' }, label: 'Lower Duration' },
        { value: { field: 'clipProperties.duration', order: 'desc' }, label: 'Higher Duration' },
        { value: { field: 'clipProperties.imageCount', order: 'asc' }, label: 'Lower Number of Photos' },
        { value: { field: 'clipProperties.imageCount', order: 'desc' }, label: 'Higher Number of Photos' },
        { value: { field: 'ctime', order: 'desc' }, label: 'Newest First' },
        { value: { field: 'ctime', order: 'asc' }, label: 'Oldest First' },
        { value: { field: 'default', order: '' }, label: 'Sort By Default' },
        { value: { field: 'random', order: '' }, label: 'Random sorting' },
    ], []);

    const lastSortingRef = useRef(JSON.stringify(sorting));

    const handleSortChange = useCallback((ev: React.ChangeEvent<HTMLSelectElement>) => {
        ev.preventDefault();
        const value = JSON.parse(ev.target.value) as CatalogSorting;
        if (Array.isArray(value) && value.length == 2 && value !== sorting) {
            setSorting(value);
        } else if (value[0] === 'random') {
            setSortingFactor(prev => prev + 1);
        }
    }, [setSorting, sorting]);

    const renderProducts = useMemo(() => (
        productFetchResult.data.map((product: ProductType, index: number) => (
            <MemoizedCardBlock
                key={product.id || index}
                {...product}
                onWishlist={isInWishlist(product)}
                inCart={isInCart(product)}
                onVideoRef={(ref) => handleVideoRef(product.id, ref)}
                onVideoPlay={() => pauseAllExcept(product.id)}
            />
        ))
    ), [productFetchResult.data, isInWishlist, isInCart, handleVideoRef, pauseAllExcept]);

    //console.log('Current video refs:', allVideoRefs.current);

    return (
        <section className="section catalog">
            <input type="checkbox" id="show-filter" className="catalog__filter-check" />
            <div className="catalog__inner inside-wrapper">
                <div className="catalog__head">
                    <label htmlFor="show-filter" className="catalog__filter-trigger">
                        <svg role="img" className="catalog__filter-trigger-ico">
                            <use href="./img/sprite.svg#filter"></use>
                        </svg>
                        Filters
                    </label>
                    <CatalogFilterTags />
                    <div className="catalog__sort">
                        <div className="sort">
                            <select
                                className="sort__select"
                                value={JSON.stringify(sorting)}
                                onChange={handleSortChange}
                                onClick={(ev: React.MouseEvent<HTMLSelectElement>) => {
                                    const target = ev.target as HTMLSelectElement;
                                    if (target.tagName !== 'OPTION') {
                                        return;
                                    }

                                    if (target.value === lastSortingRef.current) {
                                        const event = new Event('change', { bubbles: true });
                                        target.dispatchEvent(event);
                                    } else {
                                        lastSortingRef.current = target.value;
                                    }
                                }}
                            >
                                {sortingOptions.map(({ value, label }, idx) => (
                                    <option
                                        key={idx}
                                        value={JSON.stringify([value.field, value.order])}
                                        data-ico={value.field === sorting[0] && value.order === sorting[1] ? sorting[1] || 'desc' : ''}
                                    >
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <FilterPanel />
                <div className="catalog__body">
                    {isLoading && <Loading />}
                    {!isLoading && (
                        <>
                            {productFetchResult.data.length === 0 && <CatalogNoItems />}
                            {productFetchResult.data.length > 0 && (
                                <>
                                    <div className="prodlist">
                                        {renderProducts}
                                    </div>
                                    {productFetchResult.pagination && Object.keys(productFetchResult.pagination).length > 0 && (
                                        <Pagination {...productFetchResult.pagination} />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

export default React.memo(Catalog);